// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Activity, AlertTriangle, XCircle, Inbox, Layers, Satellite, Truck, RefreshCw } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import { ErrorBanner, LoadingState, EmptyState, formatTimestamp, ClinicalStatePill } from '../../components/workflow/StatePills'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'

// D-UI-2: a funnel on every column. Each value is what the cell shows; dates stay raw.
// The state labels mirror ClinicalStatePill (StatePills.tsx does not export them); an unknown
// code falls back to the code itself, exactly as the pill does.
const CLINICAL_LABELS: Record<string, string> = {
  draft: 'Draft', submitted: 'Submitted', verified: 'Verified', ready_to_fill: 'Ready to fill',
  dispensed: 'Dispensed', out_for_delivery: 'Out for delivery', delivered: 'Delivered',
  on_hold: 'On hold', cancelled: 'Cancelled', rejected: 'Rejected',
}
const TIME_IN_STATE_FUNNELS: FunnelColumn<api.OperationalDashboard['time_in_state'][number]>[] = [
  { key: 'state', label: 'State', value: r => CLINICAL_LABELS[r.state] ?? r.state },
  { key: 'count', label: 'Count', value: r => r.count },
  { key: 'avg_min', label: 'Avg time', value: r => formatMinutes(r.avg_min) },
  { key: 'max_min', label: 'Oldest', value: r => formatMinutes(r.max_min) },
]
const CANCELLATION_FUNNELS: FunnelColumn<api.OperationalDashboard['recent_cancellations'][number]>[] = [
  { key: 'rx_number', label: 'Rx #', value: c => c.rx_number },
  { key: 'drug_name', label: 'Drug', value: c => c.drug_name },
  { key: 'patient_name', label: 'Patient', value: c => c.patient_name },
  { key: 'reason', label: 'Reason', value: c => c.reason },
  { key: 'cancelled_at', label: 'When', value: c => c.cancelled_at },
]
const DRILL_FUNNELS: FunnelColumn<api.OpsDrillRow>[] = [
  { key: 'rx_number', label: 'Rx #', value: r => r.rx_number },
  { key: 'patient_name', label: 'Patient', value: r => r.patient_name },
  { key: 'drug_name', label: 'Drug', value: r => r.drug_name },
  { key: 'status_value', label: 'Status', value: r => r.status_value },
  { key: 'since', label: 'Since', value: r => r.since },
]

export default function OperationalDashboard() {
  const navigate = useNavigate()
  const [data, setData] = useState<api.OperationalDashboard | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true); setError(null)
    api.getOperationalDashboard()
      .then(setData)
      .catch(e => setError(friendlyError(e)))
      .finally(() => setLoading(false))
  }, [])

  // D-UI-2: hooks stay above the early returns; the rows are empty until the data arrives.
  const tisFx = useTableFunnels(data?.time_in_state, TIME_IN_STATE_FUNNELS)
  const cancFx = useTableFunnels(data?.recent_cancellations, CANCELLATION_FUNNELS)

  if (loading) return <div style={pageStyle}><LoadingState label="Loading operational data…" /></div>
  if (error)   return <div style={pageStyle}><ErrorBanner message={error} /></div>
  if (!data)   return null

  return (
    <div style={pageStyle}>
      <header style={{ marginBottom: 24 }}>
        <h1 style={titleStyle}>Operational dashboard</h1>
        <p style={subtitleStyle}>Where is every Rx right now — pipeline, PMS status, delivery, refills.</p>
      </header>

      {/* ── Operations boards (D-OPSDASH-1) ── */}
      <Section icon={Layers} title="Pharmacy pipeline — WorkBench queue">
        <DrillBoard entries={data.pharmacy_queue} dim="pharmacy_queue" pill />
      </Section>

      <div style={twoColStyle}>
        <Section icon={Satellite} title="PioneerRx — Rx status">
          <DrillBoard entries={data.rx_status} dim="rx_status" />
        </Section>
        <Section icon={Satellite} title="PioneerRx — transaction status">
          <DrillBoard entries={data.tx_status} dim="tx_status" />
        </Section>
      </div>

      <div style={twoColStyle}>
        <Section icon={Truck} title="Delivery">
          <DrillBoard
            entries={[
              ...data.delivery.shipments.map(sh => ({ ...sh, value: shipmentLabel(sh.value), rawValue: sh.value })),
              { value: 'Past promise time, not shipped', count: data.delivery.past_promise_unshipped, rawValue: '__past_promise', warn: true },
              { value: 'Delivery calls pending', count: data.delivery.delivery_calls_pending, rawValue: '__delivery_calls' },
            ] as any}
            dim="shipment_status"
            dimFor={(e: any) =>
              e.rawValue === '__past_promise' ? { dim: 'past_promise' } :
              e.rawValue === '__delivery_calls' ? { dim: 'delivery_calls' } :
              { dim: 'shipment_status', value: e.rawValue }}
          />
        </Section>
        <Section icon={RefreshCw} title="Refills">
          <DrillBoard
            entries={[
              { value: 'Pending refill requests', count: data.refills.pending_requests, rawValue: '__req' },
              { value: '≤ 1 refill remaining (active)', count: data.refills.low_refills, rawValue: '__low' },
              { value: 'Rx expiring within 30 days', count: data.refills.expiring_30d, rawValue: '__exp' },
            ] as any}
            dim="refill_requests"
            dimFor={(e: any) =>
              e.rawValue === '__req' ? { dim: 'refill_requests' } :
              e.rawValue === '__low' ? { dim: 'low_refills' } :
              { dim: 'expiring_30d' }}
          />
        </Section>
      </div>

      <h2 style={{ ...cardTitleStyle, fontSize: 13, margin: '24px 0 12px', color: 'var(--color-text-mute)' }}>Bench flow</h2>

      {/* Time in state */}
      <Section icon={Inbox} title="Time in state — current snapshot">
        {data.time_in_state.length === 0 ? <EmptyState message="No active prescriptions in the queue." /> : (
          <>
          {tisFx.chips}
          <table {...tisFx.tableProps} style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>State {tisFx.funnel('state')}</th>
                <th style={thNumStyle}>Count {tisFx.funnel('count')}</th>
                <th style={thNumStyle}>Avg time {tisFx.funnel('avg_min')}</th>
                <th style={thNumStyle}>Oldest {tisFx.funnel('max_min')}</th>
              </tr>
            </thead>
            <tbody>
              {tisFx.rows.map(row => (
                <tr key={row.state}>
                  <td style={tdStyle}><ClinicalStatePill state={row.state} /></td>
                  <td style={tdNumStyle}><strong>{row.count}</strong></td>
                  <td style={tdNumStyle}>{formatMinutes(row.avg_min)}</td>
                  <td style={{ ...tdNumStyle, color: row.max_min > 24 * 60 ? '#a16207' : 'inherit' }}>
                    {formatMinutes(row.max_min)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
        )}
      </Section>

      {/* Throughput last 24h */}
      <Section icon={Activity} title="Throughput — last 24 hours">
        <ThroughputBars data={data.throughput_24h} />
      </Section>

      {/* Holds + rejects side-by-side */}
      <div style={twoColStyle}>
        <Section icon={AlertTriangle} title="Why are we holding things?">
          {data.hold_reasons.length === 0 ? <EmptyState message="No holds." /> : (
            <ReasonBars data={data.hold_reasons} />
          )}
        </Section>

        <Section icon={XCircle} title="Why are we rejecting (30 days)?">
          {data.reject_reasons_30d.length === 0 ? <EmptyState message="No rejections in the last 30 days." /> : (
            <ReasonBars data={data.reject_reasons_30d} />
          )}
        </Section>
      </div>

      {/* Recent cancellations */}
      <Section icon={XCircle} title="Recent cancellations">
        {data.recent_cancellations.length === 0 ? <EmptyState message="No cancellations yet." /> : (
          <>
          {cancFx.chips}
          <table {...cancFx.tableProps} style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Rx # {cancFx.funnel('rx_number')}</th>
                <th style={thStyle}>Drug {cancFx.funnel('drug_name')}</th>
                <th style={thStyle}>Patient {cancFx.funnel('patient_name')}</th>
                <th style={thStyle}>Reason {cancFx.funnel('reason')}</th>
                <th style={thStyle}>When {cancFx.funnel('cancelled_at')}</th>
              </tr>
            </thead>
            <tbody>
              {cancFx.rows.map(c => (
                <tr key={c.rx_id} style={trClickable} onClick={() => navigate(`/rx/${c.rx_id}`)}>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 12 }}>{c.rx_number ?? '—'}</td>
                  <td style={tdStyle}>{c.drug_name}</td>
                  <td style={tdStyle}>{c.patient_name}</td>
                  <td style={tdStyle}>{c.reason}</td>
                  <td style={tdStyle}>{formatTimestamp(c.cancelled_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
        )}
      </Section>
    </div>
  )
}

// ============================================================================
// Sub-components
// ============================================================================

const SHIPMENT_LABELS: Record<string, string> = {
  created: 'Shipment created', label_purchased: 'Label purchased', in_transit: 'In transit',
  out_for_delivery: 'Out for delivery', delivered: 'Delivered (7 days)', exception: 'Delivery exception', cancelled: 'Cancelled',
}
function shipmentLabel(v: string) { return SHIPMENT_LABELS[v] ?? v }

/** D-UI-2: the Rx behind one board row. Its own component so the funnels live at the top level
 *  of the thing that owns the rows (it is rendered inside the board's map), and so a newly
 *  opened row starts with no filter. */
function DrillTable({ rows }: { rows: api.OpsDrillRow[] }) {
  const navigate = useNavigate()
  const fx = useTableFunnels(rows, DRILL_FUNNELS)
  return (
    <>
      {fx.chips}
      <table {...fx.tableProps} style={tableStyle}>
        <thead><tr>
          <th style={thStyle}>Rx # {fx.funnel('rx_number')}</th><th style={thStyle}>Patient {fx.funnel('patient_name')}</th>
          <th style={thStyle}>Drug {fx.funnel('drug_name')}</th><th style={thStyle}>Status {fx.funnel('status_value')}</th><th style={thStyle}>Since {fx.funnel('since')}</th>
        </tr></thead>
        <tbody>
          {fx.rows.map(r => (
            <tr key={r.rx_id} style={trClickable} onClick={() => navigate(`/rx/${r.rx_id}`)}>
              <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 12 }}>{r.rx_number ?? '—'}</td>
              <td style={tdStyle}>{r.patient_name ?? '—'}</td>
              <td style={tdStyle}>{r.drug_name ?? '—'}</td>
              <td style={tdStyle}>{r.status_value ?? '—'}</td>
              <td style={tdStyle}>{r.since ? formatTimestamp(r.since) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}

/** A count board: rows of value + count; clicking a row expands the top-50 Rx behind it. */
function DrillBoard({ entries, dim, dimFor, pill }: {
  entries: (api.OpsBoardEntry & { rawValue?: string; warn?: boolean })[]
  dim: string
  dimFor?: (e: any) => { dim: string; value?: string }
  pill?: boolean
}) {
  const [open, setOpen] = useState<string | null>(null)
  const [rows, setRows] = useState<api.OpsDrillRow[]>([])
  const [drillLoading, setDrillLoading] = useState(false)

  const toggle = (e: api.OpsBoardEntry & { rawValue?: string }) => {
    const key = e.rawValue ?? e.value
    if (open === key) { setOpen(null); return }
    const t = dimFor ? dimFor(e) : { dim, value: e.value }
    setOpen(key); setRows([]); setDrillLoading(true)
    api.getOperationalRxDrill(t.dim, t.value)
      .then(r => setRows(r.rows))
      .catch(() => setRows([]))
      .finally(() => setDrillLoading(false))
  }

  if (entries.length === 0) return <EmptyState message="Nothing here right now." />
  return (
    <div>
      {entries.map(e => {
        const key = e.rawValue ?? e.value
        const isOpen = open === key
        return (
          <div key={key}>
            <button onClick={() => toggle(e)} aria-expanded={isOpen} style={{
              display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between',
              padding: '9px 14px', background: isOpen ? 'var(--color-surface-alt)' : 'transparent',
              border: 'none', borderBottom: '1px solid var(--color-border)', cursor: 'pointer',
              font: 'inherit', textAlign: 'left',
            }}>
              <span>
                {pill || e.color_bg ? (
                  <span style={{
                    display: 'inline-block', padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                    background: e.color_bg ?? 'var(--color-surface-alt)', color: e.color_text ?? 'var(--color-text)',
                  }}>{e.value}</span>
                ) : (
                  <span style={{ fontSize: 13, color: e.warn && e.count > 0 ? '#a16207' : 'var(--color-text)', fontWeight: e.warn && e.count > 0 ? 700 : 400 }}>
                    {e.warn && e.count > 0 ? '⚠ ' : ''}{e.value}
                  </span>
                )}
              </span>
              <strong style={{ fontSize: 13 }}>{e.count}</strong>
            </button>
            {isOpen && (
              <div style={{ borderBottom: '1px solid var(--color-border)', background: 'var(--color-surface-alt)' }}>
                {drillLoading ? <div style={{ padding: 12, fontSize: 12, color: 'var(--color-text-mute)' }}>Loading…</div>
                 : rows.length === 0 ? <div style={{ padding: 12, fontSize: 12, color: 'var(--color-text-mute)' }}>No Rx found.</div>
                 : <DrillTable key={key} rows={rows} />}
                {rows.length === 50 && <div style={{ padding: '6px 14px', fontSize: 11, color: 'var(--color-text-mute)' }}>Showing the 50 oldest.</div>}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function Section({ icon: Icon, title, children }: { icon: any; title: string; children: React.ReactNode }) {
  return (
    <section style={cardStyle}>
      <header style={cardHeaderStyle}>
        <Icon size={14} aria-hidden="true" />
        <h3 style={cardTitleStyle}>{title}</h3>
      </header>
      <div>{children}</div>
    </section>
  )
}

function ReasonBars({ data }: { data: { reason: string; count: number }[] }) {
  const max = Math.max(...data.map(d => d.count), 1)
  return (
    <div style={{ padding: 16 }}>
      {data.map(d => (
        <div key={d.reason} style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 3 }}>
            <span style={{ fontSize: 13, color: 'var(--color-text)' }}>{d.reason}</span>
            <strong style={{ fontSize: 13 }}>{d.count}</strong>
          </div>
          <div style={{ height: 6, background: 'var(--color-surface-alt)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${(d.count / max) * 100}%`,
              background: 'var(--color-primary, #0d9488)',
            }} />
          </div>
        </div>
      ))}
    </div>
  )
}

function ThroughputBars({ data }: { data: { hours_ago: number; verified: number; filled: number; dispensed: number }[] }) {
  const max = Math.max(...data.flatMap(d => [d.verified, d.filled, d.dispensed]), 1)
  // Show last 24h, oldest first (left → right is past → present)
  const ordered = [...data].sort((a, b) => b.hours_ago - a.hours_ago)
  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 100 }}>
        {ordered.map((d, i) => {
          const total = d.verified + d.filled + d.dispensed
          return (
            <div key={i} title={`${d.hours_ago}h ago: verified ${d.verified}, filled ${d.filled}, dispensed ${d.dispensed}`}
                 style={{ flex: 1, display: 'flex', flexDirection: 'column-reverse', justifyContent: 'flex-start' }}>
              <div style={{ height: `${(d.verified / max) * 100}%`, background: '#7dd3fc' }} />
              <div style={{ height: `${(d.filled / max) * 100}%`, background: '#0d9488' }} />
              <div style={{ height: `${(d.dispensed / max) * 100}%`, background: '#1e40af' }} />
              {total === 0 && (
                <div style={{ height: 4, background: 'var(--color-surface-alt)', alignSelf: 'flex-end' }} />
              )}
            </div>
          )
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 11, color: 'var(--color-text-mute)' }}>
        <span>24h ago</span>
        <span>now</span>
      </div>
      <div style={{ display: 'flex', gap: 14, marginTop: 8, fontSize: 11 }}>
        <Legend swatch="#7dd3fc" label="Verified" />
        <Legend swatch="#0d9488" label="Filled" />
        <Legend swatch="#1e40af" label="Dispensed" />
      </div>
    </div>
  )
}

function Legend({ swatch, label }: { swatch: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span style={{ width: 10, height: 10, background: swatch, borderRadius: 2 }} />
      {label}
    </span>
  )
}

function formatMinutes(m: number) {
  if (m < 60) return `${m}m`
  if (m < 60 * 24) return `${Math.floor(m / 60)}h ${m % 60}m`
  return `${Math.floor(m / (60 * 24))}d ${Math.floor((m % (60 * 24)) / 60)}h`
}

// ============================================================================
// Styles
// ============================================================================

const pageStyle: React.CSSProperties = { maxWidth: 'var(--page-max, 1280px)', margin: '0 auto', padding: '24px 24px 48px' }
const titleStyle: React.CSSProperties = {
  fontFamily: 'Georgia, "Source Serif Pro", serif',
  fontSize: 28, fontWeight: 700, margin: 0, letterSpacing: -0.5,
}
const subtitleStyle: React.CSSProperties = { marginTop: 6, color: 'var(--color-text-soft)', fontSize: 14 }
const cardStyle: React.CSSProperties = {
  background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border)',
  borderRadius: 8, marginBottom: 16, overflow: 'hidden',
}
const cardHeaderStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px',
  background: 'var(--color-surface-alt)', borderBottom: '1px solid var(--color-border)',
  color: 'var(--color-text-soft)',
}
const cardTitleStyle: React.CSSProperties = {
  margin: 0, fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
}
const twoColStyle: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 16,
}
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 }
const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '10px 14px',
  borderBottom: '1px solid var(--color-border)',
  fontSize: 11, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase',
  color: 'var(--color-text-mute)',
}
const thNumStyle: React.CSSProperties = { ...thStyle, textAlign: 'right' as const }
const tdStyle: React.CSSProperties = { padding: '10px 14px', borderBottom: '1px solid var(--color-border)' }
const tdNumStyle: React.CSSProperties = { ...tdStyle, textAlign: 'right' as const }
const trClickable: React.CSSProperties = { cursor: 'pointer' }
